import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Search, PlusCircle, MessageCircle, UserRound, LogOut, Menu, Sprout, Heart, MapPinned, Sparkles, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n/I18nContext";

function NavItem({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          isActive ? "bg-stone-100 text-mabw-primary" : "text-mabw-soft hover:bg-stone-50"
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = useT();
  const initials = user && user.first_name
    ? `${user.first_name[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase()
    : "";

  return (
    <div className="min-h-screen flex flex-col bg-mabw-bg">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-mabw">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <Logo size={40} />
            <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
              MABW
            </span>
            <span className="hidden md:inline text-xs uppercase tracking-[0.2em] text-mabw-soft ml-1">
              Make A Better World
            </span>
          </Link>

          {user && (
            <nav className="hidden md:flex items-center gap-1">
              <NavItem to="/app" icon={Sprout} label={t("nav.home")} testid="nav-home" />
              <NavItem to="/app/cerca" icon={Search} label={t("nav.search")} testid="nav-search" />
              <NavItem to="/app/mappa" icon={MapPinned} label={t("nav.map")} testid="nav-map" />
              <NavItem to="/app/nuova" icon={PlusCircle} label={t("nav.new")} testid="nav-new" />
              <NavItem to="/app/messaggi" icon={MessageCircle} label={t("nav.messages")} testid="nav-messages" />
              <NavItem to="/app/profilo" icon={UserRound} label={t("nav.profile")} testid="nav-profile" />
              {user.role === "admin" && (
                <NavItem to="/app/admin/utenti" icon={ShieldCheck} label={t("admin.overline")} testid="nav-admin" />
              )}
            </nav>
          )}

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <>
                <button
                  onClick={() => navigate("/app/profilo")}
                  data-testid="header-avatar-btn"
                  className="hidden md:flex items-center gap-2"
                >
                  <Avatar className="w-9 h-9 bg-mabw-secondary text-white">
                    <AvatarFallback className="bg-mabw-secondary text-white">{initials || "U"}</AvatarFallback>
                  </Avatar>
                </button>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="hidden md:inline-flex btn-mabw-ghost"
                  title={t("nav.logout")}
                >
                  <LogOut className="w-4 h-4" /> {t("nav.logout")}
                </button>
                <button
                  onClick={() => setMobileOpen((v) => !v)}
                  className="md:hidden p-2 rounded-full hover:bg-stone-100"
                  data-testid="mobile-menu-btn"
                  aria-label="Menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-btn" className="btn-mabw-ghost">{t("nav.login")}</Link>
                <Link to="/registrati" data-testid="header-register-btn" className="btn-mabw">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
        {user && mobileOpen && (
          <div className="md:hidden border-t border-mabw px-4 py-3 flex flex-col gap-1 bg-white">
            <NavItem to="/app" icon={Sprout} label={t("nav.home")} testid="m-nav-home" />
            <NavItem to="/app/cerca" icon={Search} label={t("nav.search")} testid="m-nav-search" />
            <NavItem to="/app/mappa" icon={MapPinned} label={t("nav.map")} testid="m-nav-map" />
            <NavItem to="/app/nuova" icon={PlusCircle} label={t("nav.new")} testid="m-nav-new" />
            <NavItem to="/app/messaggi" icon={MessageCircle} label={t("nav.messages")} testid="m-nav-messages" />
            <NavItem to="/app/profilo" icon={UserRound} label={t("nav.profile")} testid="m-nav-profile" />
            {user.role === "admin" && (
              <NavItem to="/app/admin/utenti" icon={ShieldCheck} label={t("admin.overline")} testid="m-nav-admin" />
            )}
            <button onClick={logout} data-testid="m-logout-btn" className="btn-mabw-ghost mt-2 justify-start">
              <LogOut className="w-4 h-4" /> {t("nav.logout")}
            </button>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-mabw bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-sm text-mabw-soft">
          <div>
            <div className="font-semibold text-mabw-primary text-base" style={{ fontFamily: "Outfit" }}>
              MABW · Make A Better World
            </div>
            <p className="mt-1 max-w-xl">{t("footer.tagline")}</p>
          </div>
          <div className="flex flex-col md:items-end gap-2">
            <Link
              to="/muro-degli-aiuti"
              data-testid="footer-wall-link"
              className="inline-flex items-center gap-1.5 text-mabw-secondary hover:text-mabw-primary font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" /> {t("nav.wall")}
            </Link>
            <a
              href="https://gofund.me/e11966896"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-support-link"
              className="inline-flex items-center gap-1.5 text-mabw-secondary hover:text-mabw-primary font-medium transition-colors"
            >
              <Heart className="w-4 h-4" /> {t("landing.support_footer")}
            </a>
            <div className="flex items-center gap-4 text-xs text-mabw-soft mt-2">
              <Link to="/impressum" data-testid="footer-impressum-link" className="hover:text-mabw-primary transition-colors">
                {t("legal.impressum")}
              </Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-mabw-primary transition-colors">
                {t("legal.privacy")}
              </Link>
            </div>
            <div className="text-xs uppercase tracking-[0.2em]">© {new Date().getFullYear()} MABW</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
